import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import React, { ReactNode } from 'react'
import styled from 'styled-components'

type DropdownOption = {
  value: string
  label: ReactNode
  disabled?: boolean
  tooltip?: string
  logo?: React.ComponentType<React.SVGProps<SVGSVGElement>>
}

const StyledTrigger = styled(DropdownMenu.Trigger)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0 0.5rem;
  background-color: ${({ theme }) => `${theme.forecast.primary}99`};
  color: white;
  border: none;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.primary};
  }

  &:focus {
    outline: none;
  }

  &[data-state='open'] {
    background-color: ${({ theme }) => `${theme.forecast.primary}99`};
  }
`

const StyledContent = styled(DropdownMenu.Content)<{
  $transparentBackground?: boolean
}>`
  z-index: 1000;
  min-width: 10rem;
  background-color: ${({ theme, $transparentBackground }) =>
    $transparentBackground ? 'transparent' : theme.forecast.cardBackground};
  border-radius: 0.25rem;
  padding: 0.25rem;
  box-shadow:
    0 0.625rem 2.375rem -0.625rem rgba(22, 23, 24, 0.35),
    0 0.625rem 1.25rem -0.9375rem rgba(22, 23, 24, 0.2);
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;

  &[data-state='open'] {
    animation-name: slideDownAndFade;
  }

  &[data-state='closed'] {
    animation-name: slideUpAndFade;
  }

  @keyframes slideDownAndFade {
    from {
      opacity: 0;
      transform: translateY(-0.125rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideUpAndFade {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(-0.125rem);
    }
  }
`

const StyledItem = styled(DropdownMenu.Item)`
  font-size: 0.875rem;
  line-height: 1;
  color: white;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  height: 2rem;
  padding: 0 0.75rem;
  position: relative;
  user-select: none;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &[data-disabled] {
    color: #9ca3af;
    pointer-events: none;
  }

  &[data-highlighted] {
    background-color: #4b5563;
    outline: none;
  }
`

const StyledSeparator = styled(DropdownMenu.Separator)`
  height: 0.0625rem;
  background-color: #4b5563;
  margin: 0.25rem 0;
`

const ChevronIcon = styled(ChevronDownIcon)`
  transition: transform 0.2s ease;

  [data-state='open'] > & {
    transform: rotate(180deg);
  }
`

interface OptionsDropdownProps {
  options: DropdownOption[]
  onSelect?: (value: string) => void
  disabled?: boolean
  triggerText: string
  transparentBackground?: boolean
}

export const OptionsDropdown: React.FC<OptionsDropdownProps> = ({
  options,
  onSelect,
  disabled = false,
  triggerText,
  transparentBackground = true,
}) => {
  return (
    <DropdownMenu.Root>
      <StyledTrigger disabled={disabled}>
        {triggerText}
        <ChevronIcon />
      </StyledTrigger>

      <DropdownMenu.Portal>
        <StyledContent
          sideOffset={5}
          $transparentBackground={transparentBackground}>
          {options.map((option, index) => (
            <React.Fragment key={option.value}>
              <StyledItem
                disabled={option.disabled}
                onSelect={() => onSelect?.(option.value)}>
                {option.label}
              </StyledItem>
              {index < options.length - 1 && <StyledSeparator />}
            </React.Fragment>
          ))}
        </StyledContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
